import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Edit2, Trash2, Archive, ArchiveRestore, LogOut, Users, FileText, Link as LinkIcon, Key, UserPlus, MessageSquare, Eye, Sparkles, Settings, Download, Upload, File, CheckCircle } from 'lucide-react';
import axios from 'axios';
import APIKeysManagement from './APIKeysManagement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('content');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h1 className="text-xl font-bold text-gray-900">CAACI Admin Dashboard</h1>
              <p className="text-sm text-gray-500">Welcome, {user?.full_name || user?.username}</p>
            </div>
            <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2">
              <LogOut size={16} />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-8 max-w-6xl">
            <TabsTrigger value="content" className="flex items-center gap-2">
              <Settings size={16} />
              Content
            </TabsTrigger>
            <TabsTrigger value="articles" className="flex items-center gap-2">
              <FileText size={16} />
              Articles
            </TabsTrigger>
            <TabsTrigger value="resources" className="flex items-center gap-2">
              <LinkIcon size={16} />
              Resources
            </TabsTrigger>
            <TabsTrigger value="frameworks" className="flex items-center gap-2">
              <Download size={16} />
              AI OS
            </TabsTrigger>
            <TabsTrigger value="chats" className="flex items-center gap-2">
              <MessageSquare size={16} />
              Chats
            </TabsTrigger>
            <TabsTrigger value="registrations" className="flex items-center gap-2">
              <UserPlus size={16} />
              Registrations
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users size={16} />
              Users
            </TabsTrigger>
            <TabsTrigger value="api-keys" className="flex items-center gap-2">
              <Key size={16} />
              API Keys
            </TabsTrigger>
          </TabsList>

          <TabsContent value="content">
            <ContentManagement getAuthHeader={getAuthHeader} />
          </TabsContent>

          <TabsContent value="articles">
            <ArticlesManagement getAuthHeader={getAuthHeader} />
          </TabsContent>

          <TabsContent value="resources">
            <ResourcesManagement getAuthHeader={getAuthHeader} />
          </TabsContent>

          <TabsContent value="frameworks">
            <FrameworksManagement getAuthHeader={getAuthHeader} />
          </TabsContent>

          <TabsContent value="chats">
            <ChatsManagement getAuthHeader={getAuthHeader} />
          </TabsContent>

          <TabsContent value="registrations">
            <RegistrationsManagement getAuthHeader={getAuthHeader} />
          </TabsContent>

          <TabsContent value="users">
            <UsersManagement getAuthHeader={getAuthHeader} />
          </TabsContent>

          <TabsContent value="api-keys">
            <APIKeysManagement getAuthHeader={getAuthHeader} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Content Management Component
const ContentManagement = ({ getAuthHeader }) => {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  const fetchContent = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/content`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleChange = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/content`, content, {
        headers: getAuthHeader()
      });
      alert('Content saved successfully!');
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Error saving content: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    { id: 'hero', label: 'Hero Section' },
    { id: 'about', label: 'About Section' },
    { id: 'services', label: 'Services Section' },
    { id: 'research', label: 'Research Section' },
    { id: 'resources', label: 'Resources Section' },
    { id: 'contact', label: 'Contact Section' },
    { id: 'footer', label: 'Footer' },
    { id: 'navigation', label: 'Navigation' },
  ];

  const renderField = (field, label, multiline = false) => (
    <div key={field} className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {multiline ? (
        <Textarea
          value={content[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          className="w-full min-h-[100px]"
        />
      ) : (
        <Input
          value={content[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          className="w-full"
        />
      )}
    </div>
  );

  const renderSectionFields = () => {
    switch (activeSection) {
      case 'hero':
        return (
          <>
            {renderField('hero_title', 'Main Title')}
            {renderField('hero_subtitle', 'Subtitle', true)}
            {renderField('hero_cta_primary', 'Primary Button Text')}
            {renderField('hero_cta_secondary', 'Secondary Button Text')}
          </>
        );
      case 'about':
        return (
          <>
            {renderField('about_title', 'Section Title (include pronunciation like "CAACI (Kay-See)")')}
            {renderField('about_description', 'Main Description', true)}
            {renderField('about_mission_title', 'Mission Title')}
            {renderField('about_mission_text', 'Mission Text', true)}
            {renderField('about_vision_title', 'Vision Title')}
            {renderField('about_vision_text', 'Vision Text', true)}
          </>
        );
      case 'services':
        return (
          <>
            {renderField('services_title', 'Section Title')}
            {renderField('services_subtitle', 'Section Subtitle')}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-2">Service 1: AI Strategy & Research Advisory</h4>
              {renderField('service_1_title', 'Title')}
              {renderField('service_1_description', 'Description', true)}
              <p className="text-sm text-gray-500 mt-2 mb-1">Bullet Points:</p>
              {renderField('service_1_bullet_1', 'Bullet 1')}
              {renderField('service_1_bullet_2', 'Bullet 2')}
              {renderField('service_1_bullet_3', 'Bullet 3')}
              {renderField('service_1_bullet_4', 'Bullet 4')}
            </div>
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-2">Service 2: AI Integration & Implementation</h4>
              {renderField('service_2_title', 'Title')}
              {renderField('service_2_description', 'Description', true)}
              <p className="text-sm text-gray-500 mt-2 mb-1">Bullet Points:</p>
              {renderField('service_2_bullet_1', 'Bullet 1')}
              {renderField('service_2_bullet_2', 'Bullet 2')}
              {renderField('service_2_bullet_3', 'Bullet 3')}
              {renderField('service_2_bullet_4', 'Bullet 4')}
            </div>
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-2">Service 3: Applied Research & Innovation</h4>
              {renderField('service_3_title', 'Title')}
              {renderField('service_3_description', 'Description', true)}
              <p className="text-sm text-gray-500 mt-2 mb-1">Bullet Points:</p>
              {renderField('service_3_bullet_1', 'Bullet 1')}
              {renderField('service_3_bullet_2', 'Bullet 2')}
              {renderField('service_3_bullet_3', 'Bullet 3')}
              {renderField('service_3_bullet_4', 'Bullet 4')}
            </div>
          </>
        );
      case 'research':
        return (
          <>
            {renderField('research_title', 'Section Title')}
            {renderField('research_subtitle', 'Section Subtitle')}
          </>
        );
      case 'resources':
        return (
          <>
            {renderField('resources_title', 'Section Title')}
            {renderField('resources_subtitle', 'Section Subtitle')}
          </>
        );
      case 'contact':
        return (
          <>
            {renderField('contact_title', 'Section Title')}
            {renderField('contact_subtitle', 'Section Subtitle', true)}
            {renderField('contact_cta', 'Button Text')}
          </>
        );
      case 'footer':
        return (
          <>
            {renderField('footer_tagline', 'Tagline')}
            {renderField('footer_description', 'Description', true)}
            {renderField('footer_location', 'Location')}
            {renderField('footer_region', 'Region')}
            {renderField('footer_copyright', 'Copyright Text')}
          </>
        );
      case 'navigation':
        return (
          <>
            {renderField('nav_services', 'Services Link')}
            {renderField('nav_research', 'Research Link')}
            {renderField('nav_resources', 'Resources Link')}
            {renderField('nav_about', 'About Link')}
            {renderField('nav_connect', 'Connect Button')}
          </>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading content...</div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Section Selector */}
      <div className="lg:col-span-1">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Sections</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                    activeSection === section.id
                      ? 'bg-blue-100 text-blue-800'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content Editor */}
      <div className="lg:col-span-3">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Edit {sections.find(s => s.id === activeSection)?.label}</CardTitle>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save All Changes'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {renderSectionFields()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Articles Management Component
const ArticlesManagement = ({ getAuthHeader }) => {
  const [articles, setArticles] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentArticle, setCurrentArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Research Paper',
    readTime: '',
    publishDate: '',
    imageUrl: '',
    articleUrl: '',
    content: ''
  });

  const fetchArticles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/articles/all?include_archived=true`);
      setArticles(response.data);
    } catch (error) {
      console.error('Error fetching articles:', error);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && currentArticle) {
        await axios.put(`${API}/articles/${currentArticle.id}`, formData, {
          headers: getAuthHeader()
        });
      } else {
        await axios.post(`${API}/articles`, formData, {
          headers: getAuthHeader()
        });
      }
      resetForm();
      fetchArticles();
    } catch (error) {
      console.error('Error saving article:', error);
      alert('Error saving article: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (article) => {
    setCurrentArticle(article);
    setFormData({
      title: article.title,
      description: article.description,
      category: article.category,
      readTime: article.readTime,
      publishDate: article.publishDate,
      imageUrl: article.imageUrl || '',
      articleUrl: article.articleUrl || '',
      content: article.content || ''
    });
    setIsEditing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (articleId) => {
    if (window.confirm('Delete this article permanently?')) {
      try {
        await axios.delete(`${API}/articles/${articleId}`, {
          headers: getAuthHeader()
        });
        fetchArticles();
      } catch (error) {
        console.error('Error deleting article:', error);
      }
    }
  };

  const handleToggleArchive = async (articleId, currentStatus) => {
    try {
      await axios.put(`${API}/articles/${articleId}/archive?archive=${!currentStatus}`, {}, {
        headers: getAuthHeader()
      });
      fetchArticles();
    } catch (error) {
      console.error('Error toggling archive:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'Research Paper',
      readTime: '',
      publishDate: '',
      imageUrl: '',
      articleUrl: '',
      content: ''
    });
    setIsEditing(false);
    setCurrentArticle(null);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1">
        <Card>
          <CardHeader>
            <CardTitle>{isEditing ? 'Edit Article' : 'Add New Article'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <Input
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Article title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description *</label>
                <Textarea
                  required
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Category *</label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option>Research Paper</option>
                  <option>Industry Report</option>
                  <option>White Paper</option>
                  <option>Guide</option>
                  <option>Case Study</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Read Time *</label>
                  <Input
                    required
                    value={formData.readTime}
                    onChange={(e) => setFormData({ ...formData, readTime: e.target.value })}
                    placeholder="15 min"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date *</label>
                  <Input
                    required
                    value={formData.publishDate}
                    onChange={(e) => setFormData({ ...formData, publishDate: e.target.value })}
                    placeholder="Jan 2025"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Image URL (optional - AI generates if empty)</label>
                <Input
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                  placeholder="Leave empty for AI-generated image"
                />
                <p className="text-xs text-gray-500 mt-1">Leave blank to auto-generate a copyright-free AI image</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Article Link</label>
                <Input
                  value={formData.articleUrl}
                  onChange={(e) => setFormData({ ...formData, articleUrl: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1">
                  {isEditing ? 'Update' : 'Create'}
                </Button>
                {isEditing && (
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-2">
        <Card>
          <CardHeader>
            <CardTitle>All Articles ({articles.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {articles.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No articles yet</p>
              ) : (
                articles.map((article) => (
                  <div
                    key={article.id}
                    className={`border rounded-lg p-4 ${
                      article.isArchived ? 'bg-gray-50' : 'bg-white'
                    }`}
                  >
                    <div className="flex justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-bold">{article.title}</h3>
                          {article.isArchived && (
                            <span className="text-xs bg-gray-200 px-2 py-1 rounded">Archived</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{article.description}</p>
                        <div className="flex gap-2 text-xs flex-wrap">
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">{article.category}</span>
                          <span className="text-gray-500">{article.readTime} • {article.publishDate}</span>
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded">{article.viewCount || 0} views</span>
                        </div>
                      </div>
                      <div className="flex gap-1 ml-4">
                        <button
                          onClick={() => handleEdit(article)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleToggleArchive(article.id, article.isArchived)}
                          className="p-2 text-orange-600 hover:bg-orange-50 rounded"
                          title={article.isArchived ? 'Unarchive' : 'Archive'}
                        >
                          {article.isArchived ? <ArchiveRestore size={16} /> : <Archive size={16} />}
                        </button>
                        <button
                          onClick={() => handleDelete(article.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Resources Management Component
const ResourcesManagement = ({ getAuthHeader }) => {
  const [resources, setResources] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentResource, setCurrentResource] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    url: '',
    category: 'Victorian Government',
    section: 'Victorian Government AI Resources',
    order: 0
  });

  const fetchResources = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/resources?active_only=false`);
      setResources(response.data);
    } catch (error) {
      console.error('Error fetching resources:', error);
    }
  }, []);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && currentResource) {
        await axios.put(`${API}/resources/${currentResource.id}`, formData, {
          headers: getAuthHeader()
        });
      } else {
        await axios.post(`${API}/resources`, formData, {
          headers: getAuthHeader()
        });
      }
      resetForm();
      fetchResources();
    } catch (error) {
      console.error('Error saving resource:', error);
      alert('Error saving resource: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (resource) => {
    setCurrentResource(resource);
    setFormData({
      title: resource.title,
      description: resource.description,
      url: resource.url,
      category: resource.category,
      section: resource.section,
      order: resource.order
    });
    setIsEditing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (resourceId) => {
    if (window.confirm('Delete this resource permanently?')) {
      try {
        await axios.delete(`${API}/resources/${resourceId}`, {
          headers: getAuthHeader()
        });
        fetchResources();
      } catch (error) {
        console.error('Error deleting resource:', error);
      }
    }
  };

  const handleToggleActive = async (resourceId, currentStatus) => {
    try {
      await axios.put(`${API}/resources/${resourceId}`, 
        { isActive: !currentStatus },
        { headers: getAuthHeader() }
      );
      fetchResources();
    } catch (error) {
      console.error('Error toggling resource:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      url: '',
      category: 'Victorian Government',
      section: 'Victorian Government AI Resources',
      order: 0
    });
    setIsEditing(false);
    setCurrentResource(null);
  };

  const sections = [
    'Victorian Government AI Resources',
    'Australian AI Initiatives',
    'Industry Research & Insights'
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1">
        <Card>
          <CardHeader>
            <CardTitle>{isEditing ? 'Edit Resource' : 'Add New Resource'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <Input
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Resource title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description *</label>
                <Textarea
                  required
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">URL *</label>
                <Input
                  required
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Section *</label>
                <select
                  required
                  value={formData.section}
                  onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  {sections.map(section => (
                    <option key={section} value={section}>{section}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="e.g., Victorian Government"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Order (for sorting)</label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1">
                  {isEditing ? 'Update' : 'Create'}
                </Button>
                {isEditing && (
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-2">
        <Card>
          <CardHeader>
            <CardTitle>All Resources ({resources.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {resources.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No resources yet</p>
              ) : (
                resources.map((resource) => (
                  <div
                    key={resource.id}
                    className={`border rounded-lg p-4 ${
                      resource.isActive ? 'bg-white' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-bold">{resource.title}</h3>
                          {!resource.isActive && (
                            <span className="text-xs bg-gray-200 px-2 py-1 rounded">Inactive</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{resource.description}</p>
                        <div className="flex gap-2 text-xs flex-wrap">
                          <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">{resource.section}</span>
                          <a href={resource.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            {resource.url.substring(0, 50)}...
                          </a>
                        </div>
                      </div>
                      <div className="flex gap-1 ml-4">
                        <button
                          onClick={() => handleEdit(resource)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleToggleActive(resource.id, resource.isActive)}
                          className="p-2 text-orange-600 hover:bg-orange-50 rounded"
                          title={resource.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {resource.isActive ? <Archive size={16} /> : <ArchiveRestore size={16} />}
                        </button>
                        <button
                          onClick={() => handleDelete(resource.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Chats Management Component
const ChatsManagement = ({ getAuthHeader }) => {
  const [chatSessions, setChatSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState(null);
  const [generatingSummary, setGeneratingSummary] = useState(null);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [togglingChat, setTogglingChat] = useState(false);

  const fetchChatSessions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/chat-sessions`, {
        headers: getAuthHeader()
      });
      setChatSessions(response.data);
    } catch (error) {
      console.error('Error fetching chat sessions:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setChatEnabled(response.data.chat_enabled !== false);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, []);

  useEffect(() => {
    fetchChatSessions();
    fetchSettings();
  }, [fetchChatSessions, fetchSettings]);

  const handleToggleChat = async () => {
    try {
      setTogglingChat(true);
      const newStatus = !chatEnabled;
      await axios.put(
        `${API}/settings/chat?enabled=${newStatus}`,
        {},
        { headers: getAuthHeader() }
      );
      setChatEnabled(newStatus);
    } catch (error) {
      console.error('Error toggling chat:', error);
      alert('Error toggling chat: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTogglingChat(false);
    }
  };

  const handleViewChat = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/chat-sessions/${sessionId}`, {
        headers: getAuthHeader()
      });
      setSelectedChat(response.data);
    } catch (error) {
      console.error('Error fetching chat details:', error);
    }
  };

  const handleGenerateSummary = async (sessionId) => {
    try {
      setGeneratingSummary(sessionId);
      await axios.put(
        `${API}/chat-sessions/${sessionId}/summarize`,
        {},
        { headers: getAuthHeader() }
      );
      fetchChatSessions();
    } catch (error) {
      console.error('Error generating summary:', error);
      alert('Error generating summary: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGeneratingSummary(null);
    }
  };

  const handleDelete = async (sessionId) => {
    if (window.confirm('Delete this chat session permanently?')) {
      try {
        await axios.delete(`${API}/chat-sessions/${sessionId}`, {
          headers: getAuthHeader()
        });
        fetchChatSessions();
        if (selectedChat?.id === sessionId) {
          setSelectedChat(null);
        }
      } catch (error) {
        console.error('Error deleting chat:', error);
      }
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Chat Control Panel */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-lg">Chat Status</h3>
              <p className="text-sm text-gray-500">
                {chatEnabled 
                  ? "Chat is enabled - visitors can use Ask CAACI" 
                  : "Chat is disabled - Ask CAACI button is hidden"}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <span className={`text-sm font-medium ${chatEnabled ? 'text-green-600' : 'text-red-600'}`}>
                {chatEnabled ? 'Enabled' : 'Disabled'}
              </span>
              <button
                onClick={handleToggleChat}
                disabled={togglingChat}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  chatEnabled ? 'bg-green-500' : 'bg-gray-300'
                } ${togglingChat ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    chatEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chat Sessions List */}
        <div>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Chat Sessions ({chatSessions.length})</CardTitle>
                <Button onClick={fetchChatSessions} variant="outline" size="sm">
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-gray-500 text-center py-8">Loading chats...</p>
              ) : chatSessions.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No chat sessions yet</p>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {chatSessions.map((session) => (
                    <div
                      key={session.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        selectedChat?.id === session.id
                          ? 'bg-blue-50 border-blue-300'
                          : 'bg-white hover:bg-gray-50'
                      }`}
                      onClick={() => handleViewChat(session.id)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <MessageSquare size={16} className="text-blue-600" />
                            <span className="text-xs text-gray-500">
                              {session.messages?.length || 0} messages
                            </span>
                          </div>
                          
                          {session.summary ? (
                            <p className="text-sm text-gray-700 mb-2">{session.summary}</p>
                          ) : (
                          <p className="text-sm text-gray-500 italic mb-2">
                            {session.messages?.[0]?.content?.substring(0, 80)}...
                          </p>
                        )}
                        
                        <p className="text-xs text-gray-400">
                          {formatDate(session.updated_at || session.created_at)}
                        </p>
                      </div>
                      
                      <div className="flex gap-1 ml-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewChat(session.id);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title="View transcript"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGenerateSummary(session.id);
                          }}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded"
                          title="Generate AI summary"
                          disabled={generatingSummary === session.id}
                        >
                          <Sparkles size={16} className={generatingSummary === session.id ? 'animate-spin' : ''} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(session.id);
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Chat Transcript */}
      <div>
        <Card>
          <CardHeader>
            <CardTitle>
              {selectedChat ? 'Chat Transcript' : 'Select a chat to view'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedChat ? (
              <p className="text-gray-500 text-center py-8">
                Click on a chat session to view the full transcript
              </p>
            ) : (
              <div className="space-y-4">
                {selectedChat.summary && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                    <p className="text-xs font-semibold text-purple-800 mb-1">AI Summary</p>
                    <p className="text-sm text-purple-900">{selectedChat.summary}</p>
                  </div>
                )}
                
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {selectedChat.messages?.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg ${
                        msg.role === 'user'
                          ? 'bg-blue-100 ml-8'
                          : 'bg-gray-100 mr-8'
                      }`}
                    >
                      <p className="text-xs font-semibold text-gray-600 mb-1">
                        {msg.role === 'user' ? 'Visitor' : 'CAACI Assistant'}
                      </p>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{msg.content}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {formatDate(msg.timestamp)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  </div>
  );
};

// Registrations Management Component
const RegistrationsManagement = ({ getAuthHeader }) => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchRegistrations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/registrations`, {
        headers: getAuthHeader()
      });
      setRegistrations(response.data);
    } catch (error) {
      console.error('Error fetching registrations:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  const handleStatusChange = async (registrationId, newStatus) => {
    try {
      await axios.put(
        `${API}/registrations/${registrationId}/status?status=${newStatus}`,
        {},
        { headers: getAuthHeader() }
      );
      fetchRegistrations();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (registrationId) => {
    if (window.confirm('Delete this registration permanently?')) {
      try {
        await axios.delete(`${API}/registrations/${registrationId}`, {
          headers: getAuthHeader()
        });
        fetchRegistrations();
      } catch (error) {
        console.error('Error deleting registration:', error);
      }
    }
  };

  const filteredRegistrations = filter === 'all' 
    ? registrations 
    : registrations.filter(r => r.status === filter);

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'contacted': 'bg-yellow-100 text-yellow-800',
      'subscribed': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle>Visitor Registrations ({filteredRegistrations.length})</CardTitle>
            <div className="flex gap-2">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="subscribed">Subscribed</option>
              </select>
              <Button onClick={fetchRegistrations} variant="outline" size="sm">
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-gray-500 text-center py-8">Loading registrations...</p>
          ) : filteredRegistrations.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No registrations found</p>
          ) : (
            <div className="space-y-4">
              {filteredRegistrations.map((registration) => (
                <div
                  key={registration.id}
                  className="border rounded-lg p-4 bg-white hover:shadow-sm transition-shadow"
                  data-testid={`registration-${registration.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-gray-900">{registration.name}</h3>
                        <span className={`text-xs px-2 py-1 rounded capitalize ${getStatusColor(registration.status)}`}>
                          {registration.status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p><strong>Email:</strong> {registration.email}</p>
                        {registration.organization && (
                          <p><strong>Organization:</strong> {registration.organization}</p>
                        )}
                        {registration.message && (
                          <p><strong>Message:</strong> {registration.message}</p>
                        )}
                        {registration.interests && (
                          <p><strong>Interests:</strong> {registration.interests}</p>
                        )}
                        <p className="text-xs text-gray-500">
                          Registered: {new Date(registration.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex sm:flex-col gap-2 justify-end">
                      <select
                        value={registration.status}
                        onChange={(e) => handleStatusChange(registration.id, e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="subscribed">Subscribed</option>
                      </select>
                      <button
                        onClick={() => handleDelete(registration.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Users Management Component
const UsersManagement = ({ getAuthHeader }) => {
  const [users, setUsers] = useState([]);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'admin'
  });

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: getAuthHeader()
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, formData, {
        headers: getAuthHeader()
      });
      alert('User created successfully!');
      setFormData({
        username: '',
        email: '',
        full_name: '',
        password: '',
        role: 'admin'
      });
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Error creating user: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      alert('New passwords do not match!');
      return;
    }

    try {
      await axios.put(
        `${API}/auth/change-password?current_password=${encodeURIComponent(passwordForm.current_password)}&new_password=${encodeURIComponent(passwordForm.new_password)}`,
        {},
        { headers: getAuthHeader() }
      );
      alert('Password changed successfully!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setShowPasswordChange(false);
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Error changing password: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Create User Form */}
      <div>
        <Card>
          <CardHeader>
            <CardTitle>Create New Admin User</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Username *</label>
                <Input
                  required
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <Input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@caaci.com.au"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Full Name *</label>
                <Input
                  required
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Password *</label>
                <Input
                  required
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Strong password"
                  minLength={8}
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Role *</label>
                <select
                  required
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>

              <Button type="submit" className="w-full">
                <Plus size={16} className="mr-2" />
                Create User
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Change Password Section */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Change Your Password</CardTitle>
          </CardHeader>
          <CardContent>
            {!showPasswordChange ? (
              <Button onClick={() => setShowPasswordChange(true)} variant="outline" className="w-full">
                Change Password
              </Button>
            ) : (
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Current Password *</label>
                  <Input
                    required
                    type="password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    placeholder="Current password"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">New Password *</label>
                  <Input
                    required
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    placeholder="New password"
                    minLength={8}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Confirm New Password *</label>
                  <Input
                    required
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    placeholder="Confirm new password"
                    minLength={8}
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="submit" className="flex-1">
                    Update Password
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowPasswordChange(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Users List */}
      <div>
        <Card>
          <CardHeader>
            <CardTitle>All Users ({users.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {users.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No users found</p>
              ) : (
                users.map((user) => (
                  <div key={user.id} className="border rounded-lg p-4 bg-white">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-bold text-gray-900">{user.full_name}</h3>
                          <span className={`text-xs px-2 py-1 rounded ${
                            user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p><strong>Username:</strong> {user.username}</p>
                          <p><strong>Email:</strong> {user.email}</p>
                          <p><strong>Role:</strong> <span className="capitalize">{user.role}</span></p>
                          <p className="text-xs text-gray-500">
                            Created: {new Date(user.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Frameworks Management Component (AI Operating System)
const FrameworksManagement = ({ getAuthHeader }) => {
  const [registrations, setRegistrations] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [stats, setStats] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('files');
  const [uploading, setUploading] = useState({});
  const fileInputRefs = useRef({});

  // Framework definitions matching the AI OS page
  const frameworkClusters = [
    {
      id: 'strategy',
      title: 'Strategy & Readiness',
      frameworks: [
        { id: 'ai-readiness-snapshot', name: 'AI Readiness Snapshot', access: 'public' },
        { id: 'full-ai-readiness-assessment', name: 'Full AI Readiness Assessment', access: 'registered' },
        { id: 'use-case-prioritisation-matrix', name: 'Use Case Prioritisation Matrix', access: 'registered' },
      ]
    },
    {
      id: 'workflow',
      title: 'Workflow Intelligence',
      frameworks: [
        { id: 'ai-project-canvas', name: 'AI Project Canvas', access: 'public' },
        { id: 'workflow-redesign-framework', name: 'Workflow Redesign Framework', access: 'registered' },
        { id: 'hitl-aitl-design-framework', name: 'HITL / AITL Design Framework', access: 'registered' },
      ]
    },
    {
      id: 'governance',
      title: 'Governance & Risk',
      frameworks: [
        { id: 'ai-risk-register', name: 'AI Risk Register', access: 'registered' },
        { id: 'ai-governance-framework', name: 'AI Governance Framework', access: 'registered' },
        { id: 'model-monitoring-drift-framework', name: 'Model Monitoring & Drift Framework', access: 'registered' },
      ]
    },
    {
      id: 'decision',
      title: 'Decision Infrastructure',
      frameworks: [
        { id: 'value-realisation-tracker', name: 'Value Realisation Tracker', access: 'public' },
        { id: 'decision-audit-framework', name: 'Decision Audit Framework', access: 'registered' },
        { id: 'ai-evaluation-framework', name: 'AI Evaluation Framework', access: 'registered' },
      ]
    }
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeader();
      
      const [regsRes, downloadsRes, statsRes, filesRes] = await Promise.all([
        axios.get(`${API}/framework-registrations`, { headers }),
        axios.get(`${API}/framework-downloads`, { headers }),
        axios.get(`${API}/framework-stats`, { headers }),
        axios.get(`${API}/framework-files`, { headers })
      ]);
      
      setRegistrations(regsRes.data);
      setDownloads(downloadsRes.data);
      setStats(statsRes.data);
      
      // Convert files array to lookup object
      const filesMap = {};
      filesRes.data.forEach(f => {
        filesMap[`${f.cluster}_${f.framework_id}`] = f;
      });
      setUploadedFiles(filesMap);
    } catch (error) {
      console.error('Error fetching framework data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFileUpload = async (clusterId, frameworkId, file) => {
    const key = `${clusterId}_${frameworkId}`;
    setUploading(prev => ({ ...prev, [key]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/framework-upload/${clusterId}/${frameworkId}`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Update uploaded files state
      setUploadedFiles(prev => ({
        ...prev,
        [key]: {
          cluster: clusterId,
          framework_id: frameworkId,
          original_filename: file.name,
          file_path: response.data.path,
          uploaded_at: new Date().toISOString()
        }
      }));

      alert(`File uploaded successfully: ${file.name}`);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleDeleteFile = async (clusterId, frameworkId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    const key = `${clusterId}_${frameworkId}`;
    try {
      await axios.delete(`${API}/framework-file/${clusterId}/${frameworkId}`, {
        headers: getAuthHeader()
      });

      setUploadedFiles(prev => {
        const newFiles = { ...prev };
        delete newFiles[key];
        return newFiles;
      });

      alert('File deleted successfully');
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete file.');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="text-center py-12">Loading framework data...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-gray-900">{stats.total_registrations}</div>
              <p className="text-sm text-gray-500">Email Registrations</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-gray-900">{stats.total_downloads}</div>
              <p className="text-sm text-gray-500">Total Downloads</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">
                {Object.keys(uploadedFiles).length}
              </div>
              <p className="text-sm text-gray-500">Files Uploaded</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-orange-600">
                {12 - Object.keys(uploadedFiles).length}
              </div>
              <p className="text-sm text-gray-500">Files Pending</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Toggle View */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={activeView === 'files' ? 'default' : 'outline'}
          onClick={() => setActiveView('files')}
          size="sm"
        >
          <Upload size={14} className="mr-1" />
          Upload Files
        </Button>
        <Button
          variant={activeView === 'registrations' ? 'default' : 'outline'}
          onClick={() => setActiveView('registrations')}
          size="sm"
        >
          Email Registrations ({registrations.length})
        </Button>
        <Button
          variant={activeView === 'downloads' ? 'default' : 'outline'}
          onClick={() => setActiveView('downloads')}
          size="sm"
        >
          All Downloads ({downloads.length})
        </Button>
      </div>

      {/* File Upload View */}
      {activeView === 'files' && (
        <div className="space-y-6">
          {frameworkClusters.map(cluster => (
            <Card key={cluster.id}>
              <CardHeader>
                <CardTitle className="text-lg">{cluster.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {cluster.frameworks.map(framework => {
                    const key = `${cluster.id}_${framework.id}`;
                    const uploadedFile = uploadedFiles[key];
                    const isUploading = uploading[key];

                    return (
                      <div key={framework.id} className="flex items-center justify-between p-3 bg-gray-50 border rounded">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {uploadedFile ? (
                            <CheckCircle size={18} className="text-green-600 flex-shrink-0" />
                          ) : (
                            <File size={18} className="text-gray-400 flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="font-medium text-sm text-gray-900 truncate">{framework.name}</p>
                            {uploadedFile ? (
                              <p className="text-xs text-green-600 truncate">
                                {uploadedFile.original_filename}
                              </p>
                            ) : (
                              <p className="text-xs text-gray-500">No file uploaded</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-xs px-2 py-1 rounded ${framework.access === 'public' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                            {framework.access}
                          </span>
                          <input
                            type="file"
                            accept=".pdf,.xlsx,.xls,.csv"
                            className="hidden"
                            ref={el => fileInputRefs.current[key] = el}
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                handleFileUpload(cluster.id, framework.id, e.target.files[0]);
                              }
                            }}
                          />
                          <Button
                            size="sm"
                            variant={uploadedFile ? "outline" : "default"}
                            onClick={() => fileInputRefs.current[key]?.click()}
                            disabled={isUploading}
                            className="h-8"
                          >
                            {isUploading ? (
                              <span className="flex items-center">
                                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
                                Uploading
                              </span>
                            ) : uploadedFile ? (
                              <>
                                <Upload size={14} className="mr-1" />
                                Replace
                              </>
                            ) : (
                              <>
                                <Upload size={14} className="mr-1" />
                                Upload
                              </>
                            )}
                          </Button>
                          {uploadedFile && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteFile(cluster.id, framework.id)}
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
          <p className="text-sm text-gray-500 text-center">
            Supported formats: PDF, Excel (.xlsx, .xls), CSV
          </p>
        </div>
      )}

      {/* Registrations View */}
      {activeView === 'registrations' && (
        <Card>
          <CardHeader>
            <CardTitle>Framework Email Registrations</CardTitle>
          </CardHeader>
          <CardContent>
            {registrations.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No registrations yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2 font-medium text-gray-700">Email</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-700">Framework</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-700">Cluster</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-700">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrations.map((reg, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-2">
                          <span className="font-medium text-gray-900">{reg.email}</span>
                        </td>
                        <td className="py-3 px-2 text-gray-600">{reg.framework_name}</td>
                        <td className="py-3 px-2">
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded capitalize">
                            {reg.cluster}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-gray-500 text-xs">
                          {formatDate(reg.registered_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Downloads View */}
      {activeView === 'downloads' && (
        <Card>
          <CardHeader>
            <CardTitle>All Framework Downloads</CardTitle>
          </CardHeader>
          <CardContent>
            {downloads.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No downloads yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2 font-medium text-gray-700">Email</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-700">Framework</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-700">Type</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-700">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {downloads.map((dl, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-2">
                          <span className="font-medium text-gray-900">{dl.email || '(Public)'}</span>
                        </td>
                        <td className="py-3 px-2 text-gray-600">{dl.framework_name}</td>
                        <td className="py-3 px-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            dl.access_type === 'public' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {dl.access_type}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-gray-500 text-xs">
                          {formatDate(dl.downloaded_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminDashboard;
